import { gql } from "@apollo/client";
import React from 'react';
import LoadingIcon, { SIZES } from 'components/icons/LoadingIcon.js';
import useStdQuery from "components/data/hooks/useStdQuery.js";
import ProductCatalogContext from "components/data/transit-account/ProductCatalogContext.js";
import { useLanguage } from "../Language";
import PublicAppVars from 'utils/PublicAppVars.js';

export const GET_TRANSIT_ACCOUNT_PRODUCT_CATALOG = gql`
	query transitAcctProductCatalogGET (
		$subsystemAccountReference: ID!
		$lang: String
	) {
		TransitAccountRoute {
			getProductCatalog (
				subsystemAccountReference: $subsystemAccountReference
				lang: $lang
			) {
				id
				valueIncrement
				maxAddTransitValue
				minAddTransitValue
				mediaProducts {
					id
					mediaSKU
					mediaTypeId
					mediaTypeName
					mediaGroupId
					mediaName
					mediaShortName
					mediaLongName
					mediaFee
					mediaDepositFee
					mediaReplacementFee
					mediaEnablementFee
					riderClassId
					riderClassDescription
					subsystemTokenType
					inventoryPartNumber
					waiveEnableFeeFlag
					isNegativeBalanceTravelAllowed
					writeOffClose
					enablementFeeType
					isNonReloadable
					waiveEnableFeeReasonCode
					mediaRestrictions {
						mediaRestrictionId
						mediaRestrictionName
					}
					display {
						order
						group
					}
				}
				products {
					id
					productSku
					productTypeId
					productFamilyId
					productName
					productShortName
					productLongName
					validityDuration
					validityDurationType
					validityStartDtm
					validityEndDtm
					price
					displayGroup
					displayOrder
					display {
						group
						order
					}
					permittedUsers {
						id
						operatorId
						operatorName
					}
					maxAddPass
					supportsAutoload
				}
				transitValueOptions {
					id
					name
					nameLong
					amount
					displayGroup
					displayOrder
					display {
						group
						order
					}
				}
				operators {
					id
					operatorId
					operatorName
					travelMode
				}
			}
		}
	}
`;

export const useTransitAccountProductCatalog = ({
	/**
	 * @type string(20)
	 * (Required) Unique identifier for the transit account.
	 * */
	subsystemAccountReference,

	queryOptions,
}) => {
	const language = useLanguage();
	const lang = language ?? PublicAppVars.LANGUAGE;

	const queryResult = useStdQuery(GET_TRANSIT_ACCOUNT_PRODUCT_CATALOG, {
		variables: {
			subsystemAccountReference,
			lang,
		},
		...queryOptions });

	const {
		products,
		mediaProducts,
		transitValueOptions,
		minAddTransitValue,
		maxAddTransitValue,
		valueIncrement,
		operators,
	} = queryResult?.data?.TransitAccountRoute.getProductCatalog ?? {};

	return {
		products,
		mediaProducts,
		transitValueOptions,
		minAddTransitValue,
		maxAddTransitValue,
		valueIncrement,
		operators,
		queryResult,
	};
};

const ProductCatalog = ({
	children,
	query = GET_TRANSIT_ACCOUNT_PRODUCT_CATALOG,
	subsystemAccountReference,
	spinnerSize,
	...rest
}) => {
	const language = useLanguage();
	const lang = language ?? PublicAppVars.LANGUAGE;

	const queryResults = useStdQuery(query, {
		variables: { subsystemAccountReference, lang },
		displayName: "Product Catalog Query",
		...rest,
	});

	const { data, loading } = queryResults;

	if (loading) {
		return <LoadingIcon size={spinnerSize ?? SIZES.component} />;
	}

	const {
		TransitAccountRoute: {
			getProductCatalog: {
				products,
				mediaOptions,
				transitValueOptions,
				minAddTransitValue,
				maxAddTransitValue,
				valueIncrement,
				operators,
			},
		},
	} = data;

	const productCatalogResult = {
		products,
		mediaOptions,
		transitValueOptions,
		minAddTransitValue,
		maxAddTransitValue,
		valueIncrement,
		operators,
	};

	return <ProductCatalogContext.Provider value={productCatalogResult}>
		{children(productCatalogResult, queryResults)}
	</ProductCatalogContext.Provider>;
};

export default ProductCatalog;
